var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

import React from 'react';
import PropTypes from 'prop-types';
import { SizeMe } from 'react-sizeme';
import ScalerContext from '../../context/Scaler';
import LineCollection from '../LineCollection';
import XAxis from '../XAxis';
import Annotation from '../Annotation';
import GriffPropTypes, { annotationPropType } from '../../utils/proptypes';
import Brush from '../Brush';
import AxisPlacement from '../AxisPlacement';
import multiFormat from '../../utils/multiFormat';
import Axes from '../../utils/Axes';
import { createYScale, createXScale } from '../../utils/scale-helpers';
import { stripPlaceholderDomain } from '../Scaler';
import { calculateDomainFromData } from '../DataProvider';
var propTypes = {
  height: PropTypes.number,
  annotations: PropTypes.arrayOf(annotationPropType),
  zoomable: PropTypes.bool,
  // Number => String
  xAxisFormatter: PropTypes.func,
  xAxisHeight: PropTypes.number,
  xAxisPlacement: GriffPropTypes.axisPlacement,
  // These are all provided by Griff.
  domainsByItemId: GriffPropTypes.domainsByItemId.isRequired,
  series: GriffPropTypes.multipleSeries.isRequired,
  subDomainsByItemId: GriffPropTypes.subDomainsByItemId.isRequired,
  updateDomains: GriffPropTypes.updateDomains.isRequired,
  width: PropTypes.number
};
var defaultProps = {
  width: 1,
  height: 150,
  annotations: [],
  zoomable: true,
  xAxisFormatter: multiFormat,
  xAxisHeight: 50,
  xAxisPlacement: AxisPlacement.BOTTOM
};

var _onUpdateSelection = function _onUpdateSelection(selection, _ref) {
  var series = _ref.series,
      timeDomain = _ref.timeDomain,
      updateDomains = _ref.updateDomains,
      width = _ref.width;
  var xScale = createXScale(timeDomain, width);
  var timeSubDomain = selection.map(xScale.invert).map(Number);
  updateDomains(series.reduce(function (changes, s) {
    var _extends2;

    return _extends({}, changes, (_extends2 = {}, _extends2[s.id] = {
      time: timeSubDomain
    }, _extends2));
  }, {}));
};

var getChartHeight = function getChartHeight(_ref2) {
  var height = _ref2.height,
      xAxisHeight = _ref2.xAxisHeight,
      xAxisPlacement = _ref2.xAxisPlacement;
  return height - xAxisHeight - (xAxisPlacement === AxisPlacement.BOTH ? xAxisHeight : 0);
};

var renderXAxis = function renderXAxis(position, xAxis, _ref3) {
  var xAxisPlacement = _ref3.xAxisPlacement;

  if (position === xAxisPlacement) {
    return xAxis;
  }

  if (xAxisPlacement === AxisPlacement.BOTH) {
    return React.cloneElement(xAxis, {
      xAxisPlacement: position
    });
  }

  return null;
};

var ContextChart = function ContextChart(_ref4) {
  var propsAnnotations = _ref4.annotations,
      domainsByItemId = _ref4.domainsByItemId,
      propsHeight = _ref4.height,
      series = _ref4.series,
      subDomainsByItemId = _ref4.subDomainsByItemId,
      updateDomains = _ref4.updateDomains,
      width = _ref4.width,
      xAxisFormatter = _ref4.xAxisFormatter,
      xAxisHeight = _ref4.xAxisHeight,
      xAxisPlacement = _ref4.xAxisPlacement,
      zoomable = _ref4.zoomable;

  var getYScale = function getYScale(s, height) {
    var domain = stripPlaceholderDomain(s.yDomain) || stripPlaceholderDomain(Axes.y(domainsByItemId[s.collectionId || s.id])) || calculateDomainFromData(s.data, s.yAccessor, s.y0Accessor, s.y1Accessor);
    return createYScale(domain, height);
  };

  var firstItemId = series[0].id;
  var timeDomain = Axes.time(domainsByItemId[firstItemId]);
  var timeSubDomain = Axes.time(subDomainsByItemId[firstItemId]);
  var height = getChartHeight({
    height: propsHeight,
    xAxisHeight: xAxisHeight,
    xAxisPlacement: xAxisPlacement
  });
  var xScale = createXScale(timeDomain, width);
  var selection = timeSubDomain.map(xScale);
  var annotations = propsAnnotations.map(function (a) {
    return React.createElement(Annotation, _extends({
      key: a.id
    }, a, {
      height: height,
      xScale: xScale
    }));
  });
  var xAxis = React.createElement(XAxis, {
    height: xAxisHeight,
    domain: timeDomain,
    tickFormatter: xAxisFormatter,
    placement: xAxisPlacement,
    scaled: false,
    axis: Axes.time
  });
  return React.createElement(React.Fragment, null, renderXAxis(AxisPlacement.TOP, xAxis, {
    xAxisPlacement: xAxisPlacement
  }), React.createElement('svg', {
    height: height,
    width: width,
    style: {
      width: '100%',
      display: 'block'
    }
  }, annotations, React.createElement(LineCollection, {
    series: series.map(function (s) {
      return _extends({}, s, {
        drawPoints: false
      });
    }),
    width: width,
    height: height,
    yScalerFactory: getYScale,
    scaleY: false,
    scaleX: false,
    subDomainsByItemId: subDomainsByItemId
  }), React.createElement(Brush, {
    width: width,
    height: height,
    selection: selection,
    onUpdateSelection: function onUpdateSelection(newSelection) {
      return _onUpdateSelection(newSelection, {
        series: series,
        timeDomain: timeDomain,
        updateDomains: updateDomains,
        width: width
      });
    },
    zoomable: zoomable
  })), renderXAxis(AxisPlacement.BOTTOM, xAxis, {
    xAxisPlacement: xAxisPlacement
  }));
};

ContextChart.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
ContextChart.defaultProps = defaultProps;
export default (function (props) {
  return React.createElement(ScalerContext.Consumer, null, function (_ref5) {
    var domainsByItemId = _ref5.domainsByItemId,
        subDomainsByItemId = _ref5.subDomainsByItemId,
        updateDomains = _ref5.updateDomains,
        series = _ref5.series;
    return React.createElement(SizeMe, {
      monitorWidth: true
    }, function (_ref6) {
      var size = _ref6.size;
      return React.createElement(ContextChart, _extends({
        width: size.width,
        series: series
      }, props, {
        subDomainsByItemId: subDomainsByItemId,
        domainsByItemId: domainsByItemId,
        updateDomains: updateDomains
      }));
    });
  });
});